import React, { useEffect, useState, useRef, useCallback } from 'react'
import styled from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'
import PrintIcon from '@mui/icons-material/Print'
import DownloadIcon from '@mui/icons-material/Download'
import RepeatIcon from '@mui/icons-material/Repeat'
import { CircularProgress } from '@mui/material'
import { getTaskQrCode, getExternalTaskQrCode, getRecurringTaskQrCode, getObligationTaskQrCode } from 'services/taskService'

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1500;
`

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  max-width: 460px;
  width: calc(100% - 32px);
  padding: 20px 24px 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.grey[200]};
  cursor: pointer;
  padding: 4px;

  &:hover { color: ${({ theme }) => theme.colors.haiti}; }
`

const ModeChoice = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;

  @media (max-width: 520px) {
    flex-direction: column;
  }
`

const ModeOption = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  min-height: 44px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.secondary : theme.colors.lightGrey)};
  background: ${({ $active }) => ($active ? '#e6f7f9' : '#fff')};

  &:hover { border-color: ${({ theme }) => theme.colors.secondary}; }
`

const ModeLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`

const ModeHint = styled.span`
  font-size: 12px;
  line-height: 1.45;
  color: #64748b;
`

const QrFrame = styled.div`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
`

const QrImg = styled.img`
  max-width: 320px;
  width: 100%;
  height: auto;
`

const Caption = styled.div`
  margin-top: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  text-align: center;
  word-break: break-word;
`

const SubCaption = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[200]};
  text-align: center;
`

const Actions = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: ${({ $primary, theme }) => $primary ? theme.colors.secondary : '#fff'};
  color: ${({ $primary }) => $primary ? '#fff' : '#0f172a'};
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover { opacity: 0.9; }
`

const TaskQrCodeModal = ({
  open,
  onClose,
  taskId,
  taskTitle,
  externalAssigneeId,
  title,
  subCaption,
  isRecurringSeries = false,
  isObligationTask = false
}) => {
  const [svgUrl, setSvgUrl] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [mode, setMode] = useState('occurrence')
  const objectUrlRef = useRef(null)

  const showModeChoice = (isObligationTask || isRecurringSeries) && !externalAssigneeId
  const permanentMode = showModeChoice && mode === 'permanent'

  useEffect(() => {
    if (!open) setMode('occurrence')
  }, [open])

  const fetchQr = useCallback(
    (opts) => {
      if (externalAssigneeId) {
        return getExternalTaskQrCode({ taskId, externalAssigneeId, ...opts })
      }
      if (permanentMode) {
        return isObligationTask
          ? getObligationTaskQrCode({ taskId, ...opts })
          : getRecurringTaskQrCode({ taskId, ...opts })
      }
      return getTaskQrCode({ taskId, ...opts })
    },
    [taskId, externalAssigneeId, permanentMode, isObligationTask]
  )

  useEffect(() => {
    if (!open || !taskId) return
    let cancelled = false
    setLoading(true)
    setError(null)

    fetchQr({ format: 'svg', size: 320 })
      .then((res) => {
        if (cancelled) return
        const url = URL.createObjectURL(res.data)
        objectUrlRef.current = url
        setSvgUrl(url)
      })
      .catch(() => !cancelled && setError('Could not load QR code.'))
      .finally(() => !cancelled && setLoading(false))

    return () => {
      cancelled = true
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }
  }, [open, fetchQr])

  const handleDownloadPng = async () => {
    const res = await fetchQr({ format: 'png', size: 720 })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = permanentMode
      ? `${isObligationTask ? 'obligation' : 'repeating'}-task-${taskId}.png`
      : `task-${taskId}.png`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const handlePrint = () => {
    if (!svgUrl) return
    const w = window.open('', '_blank', 'width=600,height=700')
    if (!w) return
    // Build the print document via DOM APIs so we never inject markup as a
    // string — safer and avoids any chance of accidental script injection
    // even though the SVG payload is our own server output.
    const doc = w.document
    doc.title = `Task QR: ${taskTitle || taskId}`
    const wrap = doc.createElement('div')
    wrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;justify-content:center;padding:32px;font-family:sans-serif'
    const img = doc.createElement('img')
    img.src = svgUrl
    img.style.maxWidth = '420px'
    img.style.width = '100%'
    const titleEl = doc.createElement('div')
    titleEl.style.cssText = 'margin-top:16px;font-size:14px;text-align:center'
    titleEl.textContent = taskTitle || ''
    const sub = doc.createElement('div')
    sub.style.cssText = 'margin-top:4px;font-size:11px;color:#64748b'
    sub.textContent = permanentMode
      ? (isObligationTask
        ? 'Scan to open the current task for this obligation'
        : 'Scan to open whichever round of this repeating job is due now')
      : 'Scan to open task'
    wrap.appendChild(img)
    wrap.appendChild(titleEl)
    wrap.appendChild(sub)
    doc.body.appendChild(wrap)
    img.onload = () => setTimeout(() => w.print(), 100)
  }

  if (!open) return null

  return (
    <Backdrop onClick={onClose}>
      <Card onClick={e => e.stopPropagation()}>
        <Header>
          <Title>{title || 'Scan to open this task'}</Title>
          <CloseBtn onClick={onClose}><CloseIcon /></CloseBtn>
        </Header>
        {showModeChoice && (
          <ModeChoice role='radiogroup' aria-label='Which QR code do you want?'>
            <ModeOption
              type='button'
              role='radio'
              aria-checked={mode === 'occurrence'}
              $active={mode === 'occurrence'}
              onClick={() => setMode('occurrence')}
            >
              <ModeLabel>Just this one</ModeLabel>
              <ModeHint>
                Opens this round of the job only. Once it is done, the code still opens the
                finished record.
              </ModeHint>
            </ModeOption>
            <ModeOption
              type='button'
              role='radio'
              aria-checked={mode === 'permanent'}
              $active={mode === 'permanent'}
              onClick={() => setMode('permanent')}
            >
              <ModeLabel>
                <RepeatIcon style={{ fontSize: 16 }} />
                {isObligationTask ? 'This obligation' : 'The repeating job'}
              </ModeLabel>
              <ModeHint>
                {isObligationTask
                  ? 'Never changes. Print it once and put it on site. Scanning always opens the current task for this obligation, including the ones raised later.'
                  : 'Never changes. Print it once and put it on site. Scanning always opens whichever round is due now, including the ones created later.'}
              </ModeHint>
            </ModeOption>
          </ModeChoice>
        )}
        <QrFrame>
          {loading && <CircularProgress size={28} />}
          {error && <div style={{ color: '#b00020', fontSize: 13 }}>{error}</div>}
          {!loading && !error && svgUrl && <QrImg src={svgUrl} alt="Task QR code" />}
        </QrFrame>
        {taskTitle && <Caption>{taskTitle}</Caption>}
        <SubCaption>
          {permanentMode
            ? (isObligationTask
              ? 'This code stays the same for good. Scanning it always opens the current task for this obligation. You never have to reprint it.'
              : 'This code stays the same for good. Scanning it opens whichever round of this repeating job is due now. You never have to reprint it.')
            : (subCaption || 'Scanning opens the task drawer for any signed-in user with access.')}
        </SubCaption>
        <Actions>
          <Btn onClick={handleDownloadPng} disabled={loading || !!error}>
            <DownloadIcon style={{ fontSize: 16 }} /> Download PNG
          </Btn>
          <Btn $primary onClick={handlePrint} disabled={loading || !svgUrl || !!error}>
            <PrintIcon style={{ fontSize: 16 }} /> Print
          </Btn>
        </Actions>
      </Card>
    </Backdrop>
  )
}

export default TaskQrCodeModal
